import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { trackPageView } from './firebase/trackingService';
import { Helmet } from 'react-helmet-async';

// Layouts
import Navbar from './components/layout/Navbar';
import Footer from './components/sections/Footer';
import SEO from './components/common/SEO';

// Landing page sections
import HeroSection from './components/sections/HeroSection';
import HowItWorksSection from './components/sections/HowItWorksSection';
import FeaturesSection from './components/sections/FeaturesSection';
import ScreenshotsCarousel from './components/sections/ScreenshotsCarousel';
import PricingSection from './components/sections/PricingSection';
import TestimonialsSection from './components/sections/TestimonialsSection';
import FAQSection from './components/sections/FAQSection';

import AboutSection from './components/sections/AboutSection';
import BlogPage from './components/Blog/BlogPage';
import BlogPostPage from './components/Blog/BlogPostPage';
import AdminBlogPage from './components/Blog/AdminBlogPage';
import TermsOfService from './components/pages/TermsOfService';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import Contact from './components/pages/Contact';
import CheckCar from './components/pages/CheckCar';
import Business from './components/pages/Business';

// Global styles
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Roboto:wght@400;500&family=Roboto+Mono&display=swap');
  @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: 'Roboto', sans-serif;
    background-color: #121212;
    color: #FFFFFF;
    line-height: 1.5;
    overflow-x: hidden;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
  }
  
  /* Create smooth scrolling experience */
  html {
    scroll-behavior: smooth;
  }
  
  /* Remove blue highlight on mobile tap */
  * {
    -webkit-tap-highlight-color: transparent;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
`;


const BadgesSection = styled.section`
  background-color: #121212;
  padding: 40px 24px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 30px 24px;
  }
`;

const BadgesContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const BadgeLink = styled.a`
  display: inline-block;
  transition: transform 0.3s ease, opacity 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    opacity: 0.9;
  }
  
  img {
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    
    &.uneed-badge {
      max-width: 200px;
      
      @media (max-width: 768px) {
        max-width: 160px;
      }
    }
    
    &.tinylaunch-badge {
      width: 202px;
      
      @media (max-width: 768px) {
        width: 162px;
      }
    }
  }
`;

// JSON-LD Schema for SEO
const getSchemaMarkup = (page = 'home') => {
  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "CarConsul",
    "description": "AI-powered car buying assistant with independent advice",
    "provider": {
      "@type": "Organization",
      "name": "CarConsul",
      "url": "https://carconsul.com",
      "logo": "https://carconsul.com/logo512.png",
      "sameAs": [
        "https://twitter.com/carconsul",
        "https://facebook.com/carconsul",
        "https://linkedin.com/company/carconsul"
      ]
    },
    "serviceType": "Car Buying Assistance",
    "offer": {
      "@type": "Offer",
      "price": "9.99",
      "priceCurrency": "USD"
    }
  };

  // Add page-specific schema
  switch (page) {
    case 'about':
      return {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About CarConsul",
        "description": "Learn about CarConsul's mission to revolutionize car buying with AI",
        "publisher": {
          "@type": "Organization",
          "name": "CarConsul",
          "url": "https://carconsul.com"
        }
      };
    case 'pricing':
      return {
        "@context": "https://schema.org",
        "@type": "PriceSpecification",
        "name": "CarConsul Pricing",
        "description": "Pricing plans for CarConsul's AI car buying assistant",
        "price": "9.99",
        "priceCurrency": "USD"
      };
    case 'features':
      return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "CarConsul Features",
        "description": "Features of CarConsul's AI car buying assistant",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "AI-Powered Recommendations",
            "description": "Get personalized car recommendations based on your preferences"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Price Comparison",
            "description": "Compare prices across different dealerships"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Independent Advice",
            "description": "Receive unbiased advice with no dealer affiliations"
          }
        ]
      };
    default:
      return baseSchema;
  }
};

// HomePage component combining all sections
const HomePage = () => {
  useEffect(() => {
    trackPageView('Home', window.location.pathname);
  }, []);

  return (
    <>
      <SEO 
        title="CarConsul - AI-Powered Car Buying Assistant"
        description="Get independent, AI-powered advice to help you find the perfect car at the best price"
        pagePath=""
        schemaMarkup={getSchemaMarkup('home')}
      />
      <HeroSection />
      <BadgesSection>
        <BadgesContainer>
          <BadgeLink 
            href="https://www.uneed.best/tool/carconsul"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img 
              src="https://www.uneed.best/EMBED3.png" 
              alt="Uneed Embed Badge" 
              className="uneed-badge"
              loading="lazy"
            />
          </BadgeLink>
          <BadgeLink 
            href="https://tinylaun.ch"
            target="_blank"
            rel="noopener"
          >
            <img 
              src="https://tinylaun.ch/tinylaunch_badge_launching_soon.svg" 
              alt="TinyLaunch Badge" 
              className="tinylaunch-badge"
              loading="lazy"
            />
          </BadgeLink>
        </BadgesContainer>
      </BadgesSection>
      <ScreenshotsCarousel />
      {/* <ComparisonDemoSection /> */}
      <HowItWorksSection />
      <FeaturesSection />
      <PricingSection />
      <TestimonialsSection />
      <FAQSection />
    </>
  );
};

// About page component
const AboutPage = () => {
  useEffect(() => {
    trackPageView('About', window.location.pathname);
  }, []);

  return (
    <>
      <SEO 
        title="About CarConsul - Our Mission and Team"
        description="Learn about CarConsul's mission to revolutionize car buying with AI-powered independent advice"
        pagePath="about"
        schemaMarkup={getSchemaMarkup('about')}
      />
      <AboutSection />
      
    </>
  );
};

// Pricing page component
const PricingPage = () => {
  useEffect(() => {
    trackPageView('Pricing', window.location.pathname);
  }, []);

  return (
    <>
      <SEO 
        title="CarConsul Pricing - Affordable AI Car Buying Assistance"
        description="View our transparent pricing plans for AI-powered car buying assistance"
        pagePath="pricing"
        schemaMarkup={getSchemaMarkup('pricing')}
      />
      <PricingSection />
      
    </>
  );
};

// Features page component
const FeaturesPage = () => {
  useEffect(() => {
    trackPageView('Features', window.location.pathname);
  }, []);

  return (
    <>
      <SEO 
        title="CarConsul Features - AI-Powered Car Buying Tools"
        description="Explore the powerful features of our AI car buying assistant"
        pagePath="features"
        schemaMarkup={getSchemaMarkup('features')}
      />
      <FeaturesSection />
      
    </>
  );
};

// 404 Not Found page
const NotFoundPage = () => {
  useEffect(() => {
    trackPageView('404', window.location.pathname);
  }, []);

  return (
    <>
      <SEO 
        title="Page Not Found - CarConsul"
        description="The page you are looking for does not exist"
        pagePath="404"
      />
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <h1>404 - Page Not Found</h1>
        <p>The page you are looking for does not exist or has been moved.</p>
      </div>
    </>
  );
};

// Main App component
function App() {
  return (
      <PageContainer>
        <GlobalStyle />
        <Helmet>
          <html lang="en" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Helmet>
        <Navbar />
        <MainContent>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            {/* Blog Routes */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/admin/blog" element={<AdminBlogPage />} />
            {/* Legal Pages */}
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            {/* Contact Page */}
            <Route path="/contact" element={<Contact />} />
            {/* Business Page */}
            <Route path="/business" element={<Business />} />
            {/* Car Check Feature */}
            <Route path="/check-car-i-want-to-buy" element={<CheckCar />} />
            <Route path="/404" element={<NotFoundPage />} />
            {/* Fallback route for 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </MainContent>
        <Footer />
      </PageContainer>
  );
}

export default App;
